import { useState, type FormEvent } from "react";
import { useAdminEmailDailyCounts } from "../../hooks/useAdminEmailDailyCounts";
import type { AdminEmailDailyVolumeProps } from "../../models/admin-email.models";
import { ADMIN_EMAIL_ACTION_CLASS, ADMIN_EMAIL_CONTROL_CLASS } from "../../utils/admin-email.constants";
import { getAdminEmailDateRangeError, getAdminEmailDefaultDateRange } from "../../utils/admin-email.utils";
import AdminEmailDailyChart from "./AdminEmailDailyChart";

// Start with the last 30 UTC days and apply date edits only when the user submits a valid range.
// Chart requests are separate from message pages so paging never changes the graph's totals.
const AdminEmailDailyVolume = ({ mailboxId }: AdminEmailDailyVolumeProps) => {
  const [range, setRange] = useState(getAdminEmailDefaultDateRange);
  const [draft, setDraft] = useState(range);
  const [rangeError, setRangeError] = useState<string | null>(null);
  const counts = useAdminEmailDailyCounts(mailboxId, range);

  const applyRange = (event: FormEvent) => {
    event.preventDefault();
    const error = getAdminEmailDateRangeError(draft);
    setRangeError(error);

    if (!error) {
      setRange(draft);
    }
  };

  return (
    <section
      data-test="admin-email-daily-volume"
      aria-busy={counts.loading}
      className="rounded border border-gray-200 p-4 dark:border-gray-700"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Emails received per day</h2>
          <p className="mt-1 text-xs opacity-60">Inbox totals · up to 31 days · UTC</p>
        </div>
        <form onSubmit={applyRange} data-test="admin-email-date-range" className="flex flex-wrap items-end gap-2">
          <label className="text-xs">
            <span className="mb-1 block">From</span>
            <input
              type="date"
              required
              value={draft.from}
              data-test="admin-email-date-from"
              className={ADMIN_EMAIL_CONTROL_CLASS}
              onChange={(event) => setDraft((current) => ({ ...current, from: event.target.value }))}
            />
          </label>
          <label className="text-xs">
            <span className="mb-1 block">To</span>
            <input
              type="date"
              required
              value={draft.to}
              data-test="admin-email-date-to"
              className={ADMIN_EMAIL_CONTROL_CLASS}
              onChange={(event) => setDraft((current) => ({ ...current, to: event.target.value }))}
            />
          </label>
          <button
            type="submit"
            data-test="admin-email-apply-dates"
            disabled={counts.loading}
            className={ADMIN_EMAIL_ACTION_CLASS}
          >
            Apply
          </button>
        </form>
      </div>
      {rangeError && (
        <p role="alert" data-test="admin-email-date-range-error" className="mt-3 text-xs text-[#BF0002]">
          {rangeError}
        </p>
      )}
      {counts.loading && (
        <p role="status" className="mt-4 text-sm opacity-70">
          Loading daily totals…
        </p>
      )}
      {counts.error && (
        <p role="alert" data-test="admin-email-chart-error" className="mt-4 text-sm text-[#BF0002]">
          {counts.error}
        </p>
      )}
      {counts.response && <AdminEmailDailyChart response={counts.response} />}
    </section>
  );
};

export default AdminEmailDailyVolume;
